import identifyQualifiedTraffic from "@utils/identifyQualifiedTraffic";
import { handleCustomUDL } from "@utils/UDLUtils";

// ─── Constants ────────────────────────────────────────────────────────────────

const CLASSES = {
  open: "is-open",
};

const SELECTORS = {
  enhancedSubNavBar: ".enhanced-sub-nav-bar",
  // document-scoped (lives outside the component root)
  enhancedSubNavBarPlaceholder: ".enhanced-sub-nav-bar-placeholder",
  // combines wrapper filter + direct-child chain in one selector
  enhancedSubNavBarCta:
    ".nav-bar-wrapper:not(.course-detail-cta) .enhanced-sub-nav-bar > .comp-button > .button",
  //TODO probably selector doesn't match anything
  enhancedNavBarBurger:
    ".comp-enhanced-navigation .navigation__mobile-button--hamburger",
};

// ─── Class ────────────────────────────────────────────────────────────────────

export class NavBar {
  private el: HTMLElement;
  private abortController: AbortController;

  // Cached elements
  private enhancedSubNavBar: HTMLElement | null;
  private enhancedSubNavBarPlaceholder: HTMLElement | null;

  constructor(el: HTMLElement) {
    this.el = el;
    this.abortController = new AbortController();
    this.enhancedSubNavBar =
      el.querySelector<HTMLElement>(SELECTORS.enhancedSubNavBar);
    this.enhancedSubNavBarPlaceholder = document.querySelector<HTMLElement>(
      SELECTORS.enhancedSubNavBarPlaceholder,
    );
  }

  init(): void {
    this.calculateSize();
    this.bindEvents();
  }

  destroy(): void {
    this.abortController.abort();
  }

  // ─── Event binding ──────────────────────────────────────────────────────────

  private bindEvents(): void {
    const { signal } = this.abortController;

    this.el
      .querySelectorAll<HTMLAnchorElement>(SELECTORS.enhancedSubNavBarCta)
      .forEach((cta) => {
        cta.addEventListener(
          "click",
          () => this.clickEnhancedSubNavBarCta(cta),
          { signal },
        );
      });

    this.el
      .querySelectorAll<HTMLElement>(SELECTORS.enhancedNavBarBurger)
      .forEach((burger) => {
        burger.addEventListener(
          "click",
          () => this.clickEnhancedNavBarBurger(burger),
          { signal },
        );
      });

    window.addEventListener("resize", () => this.calculateSize(), { signal });
  }

  // ─── Size calculation ────────────────────────────────────────────────────────

  private calculateSize(): void {
    if (!this.enhancedSubNavBar || !this.enhancedSubNavBarPlaceholder) return;
    this.enhancedSubNavBarPlaceholder.style.height =
      this.enhancedSubNavBar.offsetHeight + 1 + "px";
  }

  // ─── CTA click handler ───────────────────────────────────────────────────────

  private clickEnhancedSubNavBarCta(cta: HTMLAnchorElement): void {
    const link_url = cta.href;
    this.pushDataCta({
      link_url,
      link_text: cta.innerText,
      ...identifyQualifiedTraffic(link_url),
    });
  }

  // ─── Burger click handler ────────────────────────────────────────────────────

  private clickEnhancedNavBarBurger(burger: HTMLElement): void {
    if (!burger.classList.contains(CLASSES.open)) {
      this.pushDataBurger();
    }
  }

  // ─── UDL push helpers ────────────────────────────────────────────────────────

  private pushDataCta(params: Record<string, unknown>): void {
    handleCustomUDL("top_navigation_cta_click", params);
  }

  private pushDataBurger(): void {
    handleCustomUDL("mobile_navigation_open");
  }

  // ─── Static bootstrap ───────────────────────────────────────────────────────

  static initAll(root: Document | HTMLElement = document): void {
    root
      .querySelectorAll<HTMLElement>('[data-component="nav-bar"]')
      .forEach((el) => {
        new NavBar(el).init();
      });
  }
}
